import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ISimpleComboSelectionChangingEventArgs, IgxDialogComponent, IgxStepperComponent } from '@infragistics/igniteui-angular';
import { BusStop } from 'src/app/models/bus-stop';
import { PackageState } from 'src/app/models/enums/packageState';
import { Kid } from 'src/app/models/kid';
import { PackageRequest } from 'src/app/models/package-request';
import { Parent } from 'src/app/models/parent';
import { Program } from 'src/app/models/program';
import { KidService } from 'src/app/services/kid.service';
import { ProgramsService } from 'src/app/services/programs.service';
import { WaitIndicatorService } from 'src/app/services/wait-indicator.service';
import { AddPackageComponent } from '../add-package/add-package.component';
import { EditKidFormComponent } from '../edit-kid-form/edit-kid-form.component';
import { EditParentFormComponent } from '../edit-parent-form/edit-parent-form.component';
import { SendEmailComponent } from '../send-email/send-email.component';

@Component({
    selector: 'app-process-enrollment',
    templateUrl: './process-enrollment.component.html',
    styleUrl: './process-enrollment.component.scss'
})
export class ProcessEnrollmentComponent implements OnInit, OnDestroy {
    @ViewChild('stepper', { static: true, read: IgxStepperComponent })
    private stepper!: IgxStepperComponent;

    @ViewChild('addPackage', { static: true, read: AddPackageComponent })
    private addPackage!: AddPackageComponent;

    @ViewChild('sendEmail', { static: true, read: SendEmailComponent })
    private sendEmail!: SendEmailComponent;

    private isSecondParent: boolean = false;
    private busStops: BusStop[] = [];
    protected packagesAdded = false;

    @Input()
    public request?: PackageRequest;

    @Input()
    public kid?: Kid;

    @Input()
    public parent?: Parent;

    @Input()
    public secondParent?: Parent;

    @Output() public done = new EventEmitter<PackageState>();

    public kids: Kid[] = [];
    public parents: Parent[] = [];
    public programs: Program[] = [];
    public dialogText = '';

    public constructor(
        private kidService: KidService,
        private programService: ProgramsService,
        private waitIndicatorService: WaitIndicatorService,
    ) { }
    ngOnInit(): void {
        this.addPackage.done.subscribe(async v => {
            if (v == null || v.length < 1)
                return;

            this.waitIndicatorService.show();
            const isOldCustomer = await this.kidService.isOldCustomer(this.kid?.id);
            this.packagesAdded = true;
            await this.sendEmail.load();
            this.sendEmail.contextId = "enroll";
            const dayAfterTomorrow = new Date(Date.now() + 48*60*60*1000);
            let product = "";
            if (v[0].period.product != null) {
                product = (await this.programService.productById(v[0].period.product.toString())).name;
            }
            this.sendEmail.variables = {
                "kid-name": this.kid!.name,
                "parent-name": this.parent!.name.split(" ")[0],
                "program": v[0].program.name,
                "product": product,
                "dates": v[0].period.description,
                "price-brut": v[0].price.toString(),
                "deposit-amount": v[0].deposit.toString(),
                "deposit-percent": Math.round(v[0].deposit * 100 / v[0].price).toString(),
                "deposit-time": `48 ч.`,
                "deposit-date": `${dayAfterTomorrow.toLocaleDateString('bg-BG', {year: 'numeric', month: 'long', day: 'numeric'})}`,
            }
            this.sendEmail.conditions = {
                "old-customer": isOldCustomer ? 0 : 1,
            }
            this.sendEmail.receiver = this.parent?.email ?? '';
            this.stepper.navigateTo(3);
            this.waitIndicatorService.hide();
        });
    }

    ngOnDestroy(): void {
        this.kid = undefined;
        this.parent = undefined;
    }

    public async start(): Promise<void> {
        const data = await this.programService.enrollmentData();
        this.kids = data.kids;
        this.parents = data.parents;
        this.busStops = data.stops;
        this.stepper.navigateTo(0);
        this.addPackage.clear();
        this.lookUp(this.kids, this.parents);
    }

    // #region kid
    protected kidOk = (): boolean => !!this.kid;

    protected renderStop = (id: string): string => {
        const busStop = this.busStops.find(s => s.id === id.replaceAll('-', ''));
        return busStop?.name ?? '..................';
    }

    protected kidSelected = (e: ISimpleComboSelectionChangingEventArgs): void => {
        this.kid = e.newSelection;
        this.parent = this.lookUpParent(this.kid?.parents ?? [], this.request!)
    }

    protected addKid = (e: MouseEvent, kidForm: EditKidFormComponent, dialog: IgxDialogComponent): void => {
        e.stopPropagation();
        this.dialogText = 'Добави';
        kidForm.request = this.request;
        kidForm.parent = this.parent;
        kidForm.kid = undefined;
        kidForm.kids = this.kids;
        kidForm.parents = this.parents;
        kidForm.stops = this.busStops;
        dialog.open();
    }

    protected editKid = (kidForm: EditKidFormComponent, dialog: IgxDialogComponent): void => {
        this.dialogText = 'Редактирай';
        const stop = this.busStops.find(s => s.id === this.request?.stopMorning.replaceAll('-', ''));
        if (stop && !this.kid?.morningStop) {
            this.kid!.morningStop = stop;
            this.kid!.eveningStop = stop;
        }
        kidForm.request = this.request;
        kidForm.parent = this.parent;
        kidForm.kid = this.kid;
        kidForm.kids = this.kids;
        kidForm.parents = this.parents;
        kidForm.stops = this.busStops;
        dialog.open();
    }

    protected kidDone = async (kid: Kid | undefined, dialog: IgxDialogComponent): Promise<void> => {
        this.waitIndicatorService.show();
        if (kid) {
            this.kid = kid;
            const kidIndex = this.kids.findIndex(k => k.id === kid.id);
            if (kidIndex < 0) {
                this.kids = [...this.kids, kid];
            }
            this.parent = this.lookUpParent(kid.parents, this.request!)
        }
        this.updateKids(kid);
        this.parent = this.lookUpParent(this.parents, this.request);
        dialog.close();
        this.waitIndicatorService.hide();
    }

    protected archivePackage = (dialog: IgxDialogComponent): void => {
        this.request!.state = PackageState.Archived;
        this.programService.packageRequestToArchived(this.request?.id!);
        this.done.emit(PackageState.Archived);
        dialog.close();
    }
    protected deletePackage = (dialog: IgxDialogComponent): void => {
        this.programService.packageRequestToDeleted(this.request?.id!);
        this.done.emit(PackageState.Deleted);
        dialog.close();
    }

    private updateKids = (kid: Kid | undefined):void => {
        if (kid == null)
            return;
        const index = this.kids.findIndex(p => p.id === kid.id);
        if (index < 0) {
            this.kids.push(kid);
        } else {
            this.kids.splice(index, 1 , kid);
        }
        for (const parent of kid.parents ?? []) {
            this.updateParents(parent);
        }
        this.kids = [...this.kids];
    }
    // #endregion

    // #region parent
    protected parentOk = (): boolean => this.kidOk() && (!!this.parent || !! this.secondParent);

    protected parentSelected = (e: ISimpleComboSelectionChangingEventArgs): void => {
        this.parent = e.newSelection;
    }
    protected addParent = (e: MouseEvent, parentForm: EditParentFormComponent, dialog: IgxDialogComponent): void => {
        e.stopPropagation();
        this.isSecondParent = false;
        this.dialogText = 'Добави';
        parentForm.secondParent = false;
        parentForm.request = this.request;
        parentForm.kids = this.kids;
        if (this.kid) {
            parentForm.selectedKids = [this.kid];
        }
        parentForm.parent = undefined;
        dialog.open();
    }
    protected editParent = (parentForm: EditParentFormComponent, dialog: IgxDialogComponent): void => {
        this.isSecondParent = false;
        this.dialogText = 'Редактирай';
        parentForm.secondParent = false;
        parentForm.request = this.request;
        parentForm.kids = this.kids;
        if (this.kid) {
            parentForm.selectedKids = [this.kid];
        }
        parentForm.parent = this.parent;
        dialog.open();
    }
    protected secondParentSelected = (e: ISimpleComboSelectionChangingEventArgs): void => {
        this.secondParent = e.newSelection;
    }
    protected addSecondParent = (e: MouseEvent, parentForm: EditParentFormComponent, dialog: IgxDialogComponent): void => {
        e.stopPropagation();
        this.isSecondParent = true;
        this.dialogText = 'Добави';
        parentForm.request = this.request;
        parentForm.secondParent = true;
        parentForm.parent = undefined;
        parentForm.kids = this.kids;
        dialog.open();
    }
    protected editSecondParent = (parentForm: EditParentFormComponent, dialog: IgxDialogComponent): void => {
        this.dialogText = 'Редактирай';
        parentForm.secondParent = true;
        parentForm.request = this.request;
        parentForm.parent = this.secondParent;
        parentForm.kids = this.kids;
        dialog.open();
    }
    protected parentDone = async (parent: Parent | undefined, dialog: IgxDialogComponent): Promise<void> => {
        this.waitIndicatorService.show();
        if (parent) {
            if (this.isSecondParent) {
                this.secondParent = parent
            } else {
                this.parent = parent;
            }
        }
        this.updateParents(parent);
        dialog.close();
        this.waitIndicatorService.hide();
    }

    private updateParents = (parent: Parent | undefined): void => {
        if (parent == null)
            return;
        const index = this.parents.findIndex(p => p.id === parent.id);
        if (index < 0) {
            this.parents.push(parent);
        } else {
            this.parents.splice(index, 1 , parent);
        }
        this.parents = [...this.parents];
    }
    //#endregion

    // #region program
    protected programOk = (): boolean => this.parentOk() && this.packagesAdded;

    protected programStepSelected = async (e: boolean) => {
        if (!e || !this.kid || !(!!this.parent || !!this.secondParent))
            return;
        this.waitIndicatorService.show();
        await this.addPackage.loadData();
        this.addPackage.clear();
        this.addPackage.requestedProgram = this.addPackage.allPrograms.find(p => p.id === this.request?.program.replaceAll('-', ''));
        this.addPackage.request = this.request;
        this.addPackage.kid = this.kid;
        this.waitIndicatorService.hide();
    }
    //#endregion

    // #region e-mail
    protected emailStepSelected = async (e: boolean) => {
        if (!e || !this.kid || !(!!this.parent || !!this.secondParent) || !this.packagesAdded)
            return;
        this.waitIndicatorService.show();
        await this.addPackage.loadData();
        this.addPackage.clear();
        this.addPackage.requestedProgram = this.addPackage.allPrograms.find(p => p.id === this.request?.program.replaceAll('-', ''));
        this.addPackage.request = this.request;
        this.addPackage.kid = this.kid;
        this.waitIndicatorService.hide();
    }
    //#endregion // e-mail

    // #region Lookup
    private lookUp = (kids: Kid[], parents: Parent[]): void => {
        this.kid = undefined;
        this.parent = undefined;
        this.secondParent = undefined;
        if (this.request === undefined)
            return;

        this.parent = this.lookUpParent(parents, this.request);
        this.secondParent = this.lookUpSecondParent(parents, this.request);
        this.kid = this.lookUpKid(kids, this.parent, this.request);
        if (!this.kid) {
            this.kid = this.lookUpKid(kids, this.secondParent, this.request);
        }
    }

    private lookUpParent = (parents: Parent[], request: PackageRequest | undefined): Parent | undefined => {
        if (request == null)
            return undefined;
        let result: Parent | undefined = undefined;
        for (const parent of parents) {
            const parentEmail = parent.email?.trim().toLocaleLowerCase() ?? '';
            const requestEmail = request.parentEmail?.trim().toLocaleLowerCase() ?? '';
            if (parentEmail && requestEmail && parentEmail === requestEmail) {
                result = parent;
                break;
            }

            let requestPhone = request.parentPhone;
            if (this.matchPhone(parent.phone, requestPhone)) {
                result = parent;
                break;
            }

            if (this.matchName(request.parentName, parent.name)) {
                result = parent;
                break;
            }
        }

        return result;
    }

    private lookUpSecondParent = (parents: Parent[], request: PackageRequest): Parent | undefined => {
        let result: Parent | undefined = undefined;
        for (const parent of parents) {
            let requestPhone = request.secondParentPhone;
            if (this.matchPhone(parent.phone, requestPhone)) {
                result = parent;
                break;
            }

            if (this.matchName(request.secondParentName, parent.name)) {
                result = parent;
                break;
            }
        }

        return result;
    }

    private lookUpKid = (kids: Kid[], parent: Parent | undefined, request: PackageRequest): Kid | undefined => {
        let result: Kid | undefined;
        if (parent && parent.kids) {
            result = this.lookUpKidByName(parent.kids, request);
            if (result)
                return result;
        }
        return this.lookUpKidByName(kids, request);
    }

    private lookUpKidByName = (kids: Kid[], request: PackageRequest): Kid | undefined => {
        let result: Kid | undefined = undefined;
        for (const kid of kids) {
            if (this.matchName(kid.name, request.kidName)) {
                result = kid;
                break;
            }
        }
        return result;
    }

    private matchName = (first: string | undefined, second: string | undefined): boolean => {
        first = first?.trim().toLocaleLowerCase() ?? '';
        second = second?.trim().toLocaleLowerCase() ?? '';

        if (first === '' && second === '')
            return false;

        if (first === '' || second === '')
            return false;

        if (first === second)
            return true;

        if (!first || !first.includes(' ') || !second || !second.includes(' '))
            return false;

        const firstParts = first.split(' ');
        const secondParts = second.split(' ');
        if (firstParts.length === secondParts.length)
            return false;

        if (firstParts[0] !== secondParts[0])
            return false;

        firstParts.shift();
        secondParts.shift();
        const short = firstParts.length <= secondParts.length ? firstParts : secondParts;
        const long = firstParts.length > secondParts.length ? firstParts : secondParts;
        let result = false;

        short.forEach(n => {
            if (long.some(s => s && n && s === n)) {
                result = true;
                return;
            }
        });

        return result;
    }

    private matchPhone = (first: string | undefined, second: string | undefined): boolean => {
        first = first?.replaceAll(/[^\d]+/g, '') ?? '';
        first = first.substring(1);
        second = second?.replaceAll(/[^\d]+/g, '') ?? '';
        second = second.substring(1);
        if (!first || !second)
            return false;
        return first.includes(second) || second.includes(first);
    }
    // #endregion
}
